import axios, { AxiosError } from 'axios';
import { clientLog } from '../../common/logging/logs';
import { StreamLog } from '../../common/logging/StreamLog';
import parsePerformXml from './performParser';
import { EMBED_STREAM_PROVIDERS, getStreamProvider } from './utils';
import { streamErrorCodes } from './errors';
import { NikeStream } from '../../types/generated/nike-stream';
import { MessageDialog, openDialog } from '../dialogs/dialogs';
import i18n from '../../common/i18n';

let currentSessionId = null;
let fupSubscription = null;

export function startFupSub(pushClient, errorHandler) {
    fupSubscription = pushClient.subscription({
        onMessage: sessionId => {
            if (currentSessionId && sessionId !== currentSessionId) {
                errorHandler({ code: streamErrorCodes.STREAM_FUP });
            }
        }
    });
    fupSubscription.resetPushPaths(['/user-stream/']);
}

export function stopFupSub() {
    fupSubscription.close();
}

export function setCurrentSessionId(sessionId) {
    currentSessionId = sessionId;
}

export function imgProvider(data) {
    console.log('Loading IMG stream');
    return axios
        .get(data.urlServerAuth, {
            withCredentials: false
        })
        .then(_resp => {
            // @ts-ignore
            const resp = _resp as { hlsUrl: string };
            return resp.hlsUrl;
        })
        .catch(error => {
            if (error && error.status && error.status === 403) {
                return Promise.reject({ errorCode: streamErrorCodes.GEO_BLOCK });
            }
            if (error && error.status && error.status === 404) {
                return Promise.reject({ errorCode: streamErrorCodes.STREAM_NOT_AVAILABLE });
            }
            return Promise.reject(error);
        });
}

export function performWBProvider(data) {
    console.log('Loading PerformWB provider');
    return performWBAxios(data)
        .then(response => {
            return getUrl(response);
        })
        .catch(error => {
            const errorCode = error?.response?.data?.errorCode || '';
            switch (errorCode) {
                case '20210':
                    return Promise.reject({ errorCode: 'EVENT_NOT_STARTED' });
                case '20211':
                    return Promise.reject({ errorCode: 'STREAM_NOT_AVAILABLE' });
                default:
                    return Promise.reject({ errorCode: 'TECHNICAL_PROBLEM' });
            }
        });
}

function getUrl(response) {
    const streams = response?.launchInfo?.streamLauncher[0];
    if (streams?.launcherURL) {
        return streams.launcherURL;
    }

    throw new Error('No available stream URL');
}

export function performWBAxios(data) {
    console.log('Loading PERFORM WB stream');
    const outletAuthKey = '19cnooh3ivhiz1p1p2x5tyny4m';
    const stream = data?.streams.find(stream => stream.providerId === 27);
    const asset_id = stream?.streamId;
    const token = data?.urlServerAuth;
    const config = {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: false,
        params: {
            _fmt: 'json',
            _fld: 'sl,aLng,pa,sTok,mFmt,fst,fet,sst,set,lTyp,hs'
        }
    };

    return axios.get(`https://wab.performfeeds.com/livestreamlaunch/${outletAuthKey}/${asset_id}`, config);
}

export function performProvider(data) {
    console.log('Loading PERFORM stream');
    return axios
        .get(data.urlServerAuth, {
            withCredentials: false,
            transformResponse: [resp => resp]
        })
        .then(_resp => {
            // @ts-ignore
            const resp = _resp as string;
            try {
                return parsePerformXml(resp);
            } catch (e) {
                const errorMessage = 'error while parsing mediaFormat streamLaunchCodes in response\n ' + resp;
                let errorCode = null;

                if (resp.indexOf('fairusebreach') > -1) {
                    errorCode = streamErrorCodes.GEO_BLOCK;
                } else if (resp.indexOf('eventover') > -1) {
                    errorCode = streamErrorCodes.EVENT_OVER;
                } else if (resp.indexOf('eventnotstarted') > -1) {
                    errorCode = streamErrorCodes.EVENT_NOT_STARTED;
                } else if (resp.indexOf('geoblocked') > -1) {
                    errorCode = streamErrorCodes.GEO_BLOCK;
                }

                return Promise.reject({ errorCode, errorMessage });
            }
        });
}

export function betradarProvider(data) {
    return Promise.resolve(data.urlServerAuth);
}

export function newStreamsProvider(data) {
    console.log('Loading stream', data);
    return Promise.resolve(data.urlServerAuth);
}

export interface ProviderAuthResponse {
    authUrl: string;
    providerName: NikeStream.Provider;
    isEmbed: boolean;
    expirationIntroDate: string;
}

export const getProviderAuthUrl = async (data): Promise<ProviderAuthResponse> => {
    currentSessionId = data.sessionId;
    const expirationIntroDate = data.expirationIntroDate;
    const { providerName } = data;
    const providerAuthUrlHandler = getStreamProvider(providerName);
    if (!providerAuthUrlHandler) {
        return Promise.reject({
            errorMessage: 'Unknown provider, or bad response from server ' + JSON.stringify(data)
        });
    }
    const authUrl = await providerAuthUrlHandler(data);
    if (!authUrl) {
        return Promise.reject({
            errorMessage: 'Cannot resolve stream auth url from provider ' + providerName
        });
    }

    const isEmbed = EMBED_STREAM_PROVIDERS.includes(providerName) || !authUrl?.includes('m3u8');
    return Promise.resolve({ authUrl, providerName, isEmbed, expirationIntroDate });
};

export function getLiveStreamEventInfo(eventId) {
    currentSessionId = null;
    let provider = null;
    const uri = '/api/live-stream/{eventId}'.replace('{eventId}', eventId);
    return axios
        .get<never, NikeStream.StreamDataResponse>(uri)
        .then(data => {
            provider = data.providerName;
            return handleStreamResponse({ provider, data });
        })
        .then(getProviderAuthUrl)
        .catch(error => handleStreamError({ error, id: eventId, provider, uri }));
}

export type GetCmsStreamResponse = Awaited<ReturnType<typeof getCmsStream>>;
export const getCmsStream = (matchId, nikeTv = false) => {
    return new Promise<
        ProviderAuthResponse & {
            sessionId: string;
            sportId: number;
            sport: string;
        }
    >((resolve, reject) => {
        axios
            .get<never, NikeStream.StreamDataResponse>('/api/' + (nikeTv ? 'nike-tv-stream/' : 'cms-stream/') + matchId)
            .then(data => {
                if (data.errorCode || data.errorMessage) {
                    return reject({
                        value: 'INVALID',
                        code: data.errorCode || 'GENERAL',
                        params: data.errorParams
                    });
                }
                getProviderAuthUrl(data)
                    .then(resp => {
                        const streamResponse = data.streams?.find(stream => (stream.streamType = 'VIDEO'));
                        const providerResponse = {
                            ...resp,
                            sessionId: data?.sessionId,
                            expirationIntroDate: data?.expirationIntroDate,
                            sportId: streamResponse?.sportId,
                            sport: streamResponse?.sportName
                        };
                        return resolve(providerResponse);
                    })
                    .catch(error =>
                        reject({
                            value: 'INVALID',
                            code: error?.errorCode || 'GENERAL'
                        })
                    );
            })
            .catch(() => reject({ value: 'INVALID', code: 'UNKNOWN_ERROR' }));
    });
};

export const confirmEmail = () =>
    axios.post('/api/nike-tv-stream/email/confirmation').catch((error: AxiosError) => {
        openDialog(MessageDialog, { message: i18n('nui.client.param.change.error.undefined') });
    });

export const validateCmsStream = ({
    provider = '',
    restriction = null,
    introPeriod = null,
    tournamentId = null
}: {
    provider?: string;
    restriction?: string;
    introPeriod?: string;
    tournamentId?: string;
}) =>
    axios.get<never, NikeStream.StreamDataResponse>(
        '/api/cms-stream/validate/{provider}'.replace('{provider}', provider),
        {
            params: {
                restriction,
                introPeriod,
                tournamentId
            }
        }
    );

export const handleStreamResponse = ({ data }: { provider: string; data: NikeStream.StreamDataResponse }) => {
    if (data.errorCode || data.errorMessage) {
        return Promise.reject({
            errorCode: data.errorCode,
            errorParams: data.errorParams,
            errorMessage: 'Stream auth error: ' + (data.errorMessage || '[no message]')
        });
    }
    return data;
};

export const handleStreamError = ({ error, id, provider, uri }) => {
    clientLog(
        StreamLog({
            message: `Failed to play stream loader from: ${uri}, code: ${error?.errorCode}`,
            data: {
                error: {
                    provider,
                    id,
                    uri,
                    errorCode: error?.errorCode,
                    errorParams: error?.errorParams,
                    errorMessage: error?.errorMessage,
                    request: {
                        status: error?.request?.status,
                        responseURL: error?.request?.responseURL,
                        response: error?.request?.responseText?.substring(0, 150)
                    }
                }
            },
            stacktrace: error?.stacktrace || error.stack
        })
    );

    return Promise.reject(error);
};
